// One glyph per sport, so a ticket says what it is before it is read.
//
// Today used to be one kind of paper. It is two now — sports multis and racing
// all-ups on the same roll — and the words that tell them apart are the
// strategy's name in the eyebrow, which is the smallest type on the ticket and
// the last thing a thumb scrolling past reads. A ball and a flag are told apart
// at arm's length; "RACING ALL-UP" and "FAVOURITES AND TWO LONG SHOTS" are not.
//
// THE GLYPH IS NEVER THE MESSAGE. Every place one prints, the words are already
// there and unchanged — the strategy's name, the sports the row spans — and the
// icon is `aria-hidden`, 14px, `--carbon-faint`. It is a way of finding the
// line you want in a column of lines, not a thing to be decoded. "No colour
// carries meaning alone" is the app's rule; this is the same rule for shapes.
//
// THE SAME IONICONS THE TAB BAR USES, imported rather than named: `<ion-icon
// name="…">` fetches its SVG from a CDN the content-security-policy blocks, so
// a named icon is a blank square on a plane. See router.js, which imports the
// five tab glyphs for the same reason.
//
// A leaf: `engine/teams.js` for the sport table and ionicons for the shapes,
// and nothing that reaches back here.

import {
  americanFootballOutline, baseballOutline, basketballOutline, flagOutline,
  footballOutline, golfOutline, tennisballOutline,
} from 'ionicons/icons';
import { RACING_SPORTS, SPORT_LABELS, isRacingSport, sportLabel } from './engine/teams.js';

/**
 * The generic ball: a sport this app carries that ionicons has no shape for,
 * and the answer for anything it has never heard of.
 *
 * A round ball rather than nothing at all, because the glyph's job on a mixed
 * feed is to say WHICH SIDE of the pad a ticket came from — and a sports ticket
 * with no glyph beside a racing ticket with one reads as a ticket that failed
 * to load its picture.
 */
export const BALL = footballOutline;

/** The flag that drops. Racing's own, and the Racing tab's. */
export const FLAG = flagOutline;

/**
 * Sport id → its shape.
 *
 * The oval codes take `american-football`, which is the only oval ball in the
 * set — an AFL Sherrin and a rugby league ball are both closer to it than to
 * anything round. Cricket takes `baseball`, the one seamed ball ionicons draws;
 * a bat-and-ball game drawn as a soccer ball would be worse than the generic.
 *
 * Tennis and golf are here and are not on the board: the set is the six shapes
 * ionicons has for a sport, and a board that gains one should not have to come
 * back to this file to find its own glyph. Neither is reachable today.
 */
export const SPORT_GLYPHS = {
  afl: americanFootballOutline,
  nrl: americanFootballOutline,
  aleague: footballOutline,
  epl: footballOutline,
  nbl: basketballOutline,
  bbl: baseballOutline,
  tennis: tennisballOutline,
  golf: golfOutline,
  // All three codes are the same flag. A punter sorting a feed is asking "is
  // this the horses or the football", never "is this harness or greyhounds" —
  // the venue and the runner say that, on the leg.
  ...Object.fromEntries(RACING_SPORTS.map((s) => [s, flagOutline])),
};

/** The shape for one sport id. Racing is the flag; anything unknown is a ball. */
export function glyphFor(sport) {
  if (isRacingSport(sport)) return FLAG;
  return SPORT_GLYPHS[sport] ?? BALL;
}

/**
 * How many glyphs may print in one row before the rest become a count.
 *
 * Three is what fits beside a strategy's name at 320px without pushing the tier
 * off the line, and it is also about the point where a row of small shapes
 * stops being readable as a list and starts being texture.
 */
export const GLYPH_MAX = 3;

/** Lowercase, letters and digits only — "A-League" and "aleague" are one key. */
const squash = (s) => String(s ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');

/**
 * What a leg calls its sport, and what to do when it does not.
 *
 * A LEG STRAIGHT OFF THE ENGINE CARRIES `sport`; A RECORD MAY NOT. History and
 * the paper trail read frozen copies, some of them written before the field
 * existed, and all any of those has is `sportTitle` — the competition's name as
 * it was printed ("AFL", "A-League", "Horses"). So the name is mapped back
 * through the app's OWN label table rather than through a second list of
 * spellings: `sportLabel` wrote it, and this reads it.
 *
 * A title nothing recognises answers null, and null means no glyph. A shape
 * invented for a leg the app cannot place would be a guess printed as a fact.
 */
const BY_LABEL = new Map([
  ...Object.entries(SPORT_LABELS).map(([id, label]) => [squash(label), id]),
  // The two the app writes somewhere other than through `sportLabel`: a
  // competition name on a frozen leg, and the odds feed's own wording.
  ['bigbash', 'bbl'],
  ['bigbashleague', 'bbl'],
  ['australianrules', 'afl'],
  ['aussierules', 'afl'],
  ['rugbyleague', 'nrl'],
]);

export function sportOf(leg) {
  if (leg?.sport) return leg.sport;
  const title = squash(leg?.sportTitle);
  return (title && BY_LABEL.get(title)) || null;
}

/**
 * Every sport on a ticket, IN LEG ORDER and deduped.
 *
 * Leg order rather than alphabetical because the legs are printed in that order
 * directly underneath: a reader who sees a flag then a ball and then reads a
 * race followed by a football match has been told the truth twice.
 *
 * A leg with no sport on it is skipped rather than counted as an unknown one —
 * every leg the engine prints carries `sport`, and a hand-built ticket rebuilt
 * from an old record may not. A missing sport is a gap in the record, not a
 * sport, and it must not put a ball on a racing ticket.
 */
export function sportsOf(legs) {
  const seen = [];
  for (const leg of legs ?? []) {
    const sport = sportOf(leg);
    if (!sport || seen.includes(sport)) continue;
    seen.push(sport);
  }
  return seen;
}

/**
 * The glyphs to print for a ticket: `[{ sport, icon, label }]`, at most `max`
 * of them (`GLYPH_MAX` by default).
 *
 * DEDUPED BY SHAPE, not only by sport. A four-leg ticket of two AFL games and
 * two NRL games is one oval ball, not two identical ones side by side — the
 * second says nothing the first did not, and two of the same shape reads as a
 * leg count, which it is not.
 *
 * `max` is a WIDTH decision and belongs to the caller: a History row already
 * spells its sports out in words ("AFL · NRL +2") and has a figure and a stamp
 * to fit on the same line, so it takes two and the feed takes three.
 */
export function glyphsFor(legs, { max = GLYPH_MAX } = {}) {
  const out = [];
  for (const sport of sportsOf(legs)) {
    const icon = glyphFor(sport);
    if (out.some((g) => g.icon === icon)) continue;
    out.push({ sport, icon, label: sportLabel(sport) });
  }
  return out.slice(0, max);
}

/**
 * How many shapes did not fit — the `+2` at the end of the row.
 *
 * Counted in SHAPES, so it agrees with what is drawn: a five-leg ticket across
 * AFL, NRL, NBL, EPL and the horses carries four shapes (one oval, one hoop,
 * one round ball, one flag), three print and the count says one.
 */
export function moreGlyphs(legs, { max = GLYPH_MAX } = {}) {
  const shapes = new Set(sportsOf(legs).map(glyphFor));
  return Math.max(0, shapes.size - max);
}

/** "AFL", "AFL and NRL", "Horses, NRL and AFL" — an Oxford-free list. */
function andList(words) {
  if (words.length <= 1) return words[0] ?? '';
  return `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`;
}

/**
 * What the glyphs say, in words, for the one `.sr-only` line beside them.
 *
 * EVERY sport, not the three that fit: the cap is a thing about the width of a
 * phone, and a screen reader has no width. Spelled with the app's own names for
 * them — "Horses", "A-League" — so the sentence a reader hears is the sentence
 * the Strategies screen prints.
 */
export function glyphLabel(legs) {
  return andList(sportsOf(legs).map(sportLabel));
}

/**
 * The union of a whole cluster's tickets, as glyphs — the same shape
 * `glyphsFor` answers, over every leg of every ticket in reading order.
 *
 * `entries` are timeline entries (`{ ticket: { legs } }`); anything else with a
 * `legs` array is taken as it comes, so a caller holding tickets rather than
 * entries does not have to reshape them.
 */
export const legsOfAll = (entries) => (entries ?? [])
  .flatMap((e) => e?.ticket?.legs ?? e?.legs ?? []);
